/**
 * Overlay hitboxes for flattened form fields.
 *
 * All AcroForm text fields are flattened before the PDF reaches the Syncfusion viewer so
 * every field keeps its original appearance stream (see flattenPdfFormFields). The click,
 * focus, and right-click behavior fields used to get from Syncfusion's form-field inputs is
 * provided instead by transparent divs positioned over the flattened fields, anchored to the
 * viewer's page divs and rescaled on zoom/scroll/resize — the same overlay pattern used by
 * the statement icons. Every text field gets a focusable hitbox (matching the old behavior
 * where every field was a clickable input with a focus border); drilldown/descriptive fields
 * additionally get the right-click context menu, and statement fields host the statement
 * icon overlay.
 */

import i18n from 'i18next';

export interface IInteractiveFieldBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface IInteractiveFormField {
    name: string;
    pageIndex: number;
    /**
     * Widget rectangle in PDF points, top-left origin, in the space of the page as the
     * viewer displays it (ej2-pdf field.bounds directly; mapped through the rotation
     * transform for pages the viewer displays rotated — see mapTopLeftBoundsForRotation90).
     */
    bounds: IInteractiveFieldBounds;
    /** Displayed page width in PDF points, used to derive the render scale from the page div width. */
    pageWidth: number;
    dataName?: string;
    row?: number;
    tooltip?: string;
    isDrilldown: boolean;
    isDescriptiveData: boolean;
    /** Whether this field can carry an override value — the same fields eligible for form-field comments. */
    overridable: boolean;
    /**
     * Comment presence for this field's underlying report cell, resolved once when the form
     * is flattened (see flattenPdfFormFields). Not re-derived on comment add/resolve within
     * the same form view — refreshes on the next form load/reload, matching the cost/benefit
     * of the grid's own per-cell indicator, which likewise needs an explicit refresh trigger.
     */
    hasUnresolvedComments: boolean;
    hasOnlyResolvedComments: boolean;
    /** The report cell's displayed value (e.g. a formatted amount) - used as the base for the
     *  comment-state aria-label below, mirroring what the report grid announces for the same cell. */
    cellDisplayValue?: string;
    /**
     * Numeric fields are conventionally right-aligned, which puts their last digit right where
     * the top-right comment indicator sits - so for these, draw the indicator in the top-left
     * corner instead, where right-aligned text is least likely to reach.
     */
    isNumeric?: boolean;
    /**
     * Which border the hitbox should draw for the field, resolved in flattenPdfFormFields
     * from the border rule the backend reports per field (ReviewFormField.pdfBorderStyle)
     * plus the 2026 interactive-blue rule: 'default' (thin black) upgrades to 'interactive'
     * (light blue) for drilldown-allowed / flag-gated descriptive-data fields;
     * 'numberedStatement' is the backend's blue double-width rule; 'none' draws nothing
     * (barcode/marker fields, or widgets that keep an authored border).
     */
    border: InteractiveFieldBorder;
    isStatement: boolean;
}

export type InteractiveFieldBorder = 'none' | 'default' | 'interactive' | 'numberedStatement';

export interface IInteractiveFieldHitboxHandlers {
    /**
     * The handler decides whether to open an app menu — and must call event.preventDefault()
     * when it does. When it opens nothing (e.g. a drilldown field on an excluded column),
     * leaving the event alone lets the browser's default context menu appear.
     */
    onContextMenu: (field: IInteractiveFormField, element: HTMLElement, event: MouseEvent) => void;
    onStatementFieldElement?: (field: IInteractiveFormField, element: HTMLElement) => void;
}

export interface IInteractiveFieldHitboxOptions {
    /**
     * Border colors per field border kind. A hitbox draws a border only when the color for
     * its kind is provided — pass nothing when the PDF still carries authored field borders
     * (otherwise fields would show a double border). Border widths mirror the backend rules:
     * 1px for default/interactive, 2px for numbered-statement.
     */
    defaultBorderColor?: string;
    interactiveBorderColor?: string;
    numberedStatementBorderColor?: string;
}

const HITBOX_ATTRIBUTE = 'data-interactive-field-hitbox';
// Design-time size (at zoom scale 1) for the comment indicator triangle - see its use below.
const COMMENT_INDICATOR_SIZE_PX = 15;
// Hitbox elements are created once and reused across syncs (see syncInteractiveFieldHitboxes),
// but a field's eligibility (isDrilldown/isDescriptiveData/overridable) can change after that -
// e.g. overridable flips once the collaboration-UI claims query resolves, or once the selected
// entity resolves to a different report column. The context-menu listener attached below needs
// the LATEST field, not the one captured in the closure at creation time, so syncInteractiveFieldHitboxes
// keeps this map updated with the current field for every hitbox on every sync call.
const hitboxFields = new WeakMap<HTMLDivElement, IInteractiveFormField>();
// Same staleness problem as hitboxFields above, but for the handlers themselves: onContextMenu
// closes over whatever isCollaborationUIEnabled/isFormFieldCommentsEnabled etc. were current when
// the hitbox was first created, since createHitboxElement (and its event listener) only runs once
// per hitbox. Without this, a freshened field would reach a still-stale handler closure. Kept
// current the same way - every sync call updates it for every hitbox, new or reused.
const hitboxHandlers = new WeakMap<HTMLDivElement, IInteractiveFieldHitboxHandlers>();

export const getInteractiveFieldHitboxId = (fieldName: string): string => `interactiveFieldHitbox_${fieldName}`;

const getViewerContentContainer = (viewerElementId: string): HTMLElement | null => {
    const pdfViewerContainer = document.getElementById(viewerElementId);
    return (
        (pdfViewerContainer?.querySelector('.e-pv-viewer-container') as HTMLElement) ||
        (pdfViewerContainer?.querySelector('.e-pv-main-container') as HTMLElement) ||
        (pdfViewerContainer as HTMLElement)
    );
};

const createHitboxElement = (field: IInteractiveFormField, options?: IInteractiveFieldHitboxOptions): HTMLDivElement => {
    const hitbox = document.createElement('div');
    hitbox.id = getInteractiveFieldHitboxId(field.name);
    hitbox.setAttribute(HITBOX_ATTRIBUTE, field.name);
    // focusElementAfterFormModal and the statement modal flow look fields up by name
    hitbox.setAttribute('name', field.name);
    hitbox.setAttribute('data-testid', `formfield-${hitbox.id}`);
    if (field.tooltip) {
        hitbox.title = field.tooltip;
    }
    hitbox.tabIndex = 0;
    hitbox.style.position = 'absolute';
    hitbox.style.backgroundColor = 'transparent';
    hitbox.style.pointerEvents = 'auto';
    hitbox.style.cursor = 'pointer';
    hitbox.style.zIndex = '99';

    // Statement hitboxes are excluded: the statement icon overlay hides the box and
    // reshapes it to the icon's footprint, so a border would end up on the icon.
    if (!field.isStatement) {
        const borderColor =
            field.border === 'interactive'
                ? options?.interactiveBorderColor
                : field.border === 'numberedStatement'
                ? options?.numberedStatementBorderColor
                : field.border === 'default'
                ? options?.defaultBorderColor
                : undefined;
        if (borderColor) {
            const borderWidth = field.border === 'numberedStatement' ? 2 : 1;
            hitbox.style.border = `${borderWidth}px solid ${borderColor}`;
            hitbox.style.boxSizing = 'border-box';
        }
    }

    // Always attached, regardless of this field's initial eligibility - the handler reads the
    // latest field AND handlers from the maps above (kept current by syncInteractiveFieldHitboxes)
    // rather than what was captured here, so a field that becomes eligible later still gets a
    // working menu without needing its hitbox recreated.
    hitbox.addEventListener('contextmenu', (event) => {
        const currentField = hitboxFields.get(hitbox);
        const currentHandlers = hitboxHandlers.get(hitbox);
        if (currentField && currentHandlers && (currentField.isDrilldown || currentField.isDescriptiveData || currentField.overridable)) {
            currentHandlers.onContextMenu(currentField, hitbox, event);
        }
    });

    hitbox.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
        }
    });

    return hitbox;
};

/**
 * Creates missing hitboxes and (re)positions all of them. Idempotent — safe to call on
 * page render, zoom, scroll, and resize. Pages that are not rendered yet are skipped and
 * picked up on the next call.
 */
export const syncInteractiveFieldHitboxes = (
    viewerElementId: string,
    fields: IInteractiveFormField[],
    handlers: IInteractiveFieldHitboxHandlers,
    options?: IInteractiveFieldHitboxOptions
): void => {
    const contentContainer = getViewerContentContainer(viewerElementId);
    if (!contentContainer) {
        return;
    }

    if (getComputedStyle(contentContainer).position === 'static') {
        contentContainer.style.position = 'relative';
    }
    const containerRect = contentContainer.getBoundingClientRect();

    // Page divs are resolved once per page rather than per field — forms can have
    // thousands of fields.
    const pageAnchorCache = new Map<number, { scale: number; rect: DOMRect } | null>();
    const getPageAnchor = (field: IInteractiveFormField) => {
        if (!pageAnchorCache.has(field.pageIndex)) {
            const pageDiv = document.getElementById(`${viewerElementId}_pageDiv_${field.pageIndex}`);
            // getBoundingClientRect().width is fractional — clientWidth truncates to an
            // integer, which drifts hitboxes by 1-2px at the bottom/right of a page.
            const pageRect = pageDiv?.getBoundingClientRect();
            pageAnchorCache.set(
                field.pageIndex,
                pageDiv && pageRect && pageRect.width > 0 && field.pageWidth > 0
                    ? { scale: pageRect.width / field.pageWidth, rect: pageRect }
                    : null
            );
        }
        return pageAnchorCache.get(field.pageIndex) ?? null;
    };

    fields.forEach((field) => {
        const anchor = getPageAnchor(field);
        if (!anchor) {
            return;
        }

        let hitbox = document.getElementById(getInteractiveFieldHitboxId(field.name)) as HTMLDivElement | null;
        if (!hitbox) {
            hitbox = createHitboxElement(field, options);
            contentContainer.appendChild(hitbox);
        }
        hitboxFields.set(hitbox, field);
        hitboxHandlers.set(hitbox, handlers);

        const left = anchor.rect.left - containerRect.left + contentContainer.scrollLeft + field.bounds.x * anchor.scale;
        const top = anchor.rect.top - containerRect.top + contentContainer.scrollTop + field.bounds.y * anchor.scale;
        const height = field.bounds.height * anchor.scale;

        hitbox.style.left = `${left}px`;
        hitbox.style.top = `${top}px`;
        hitbox.style.width = `${field.bounds.width * anchor.scale}px`;
        hitbox.style.height = `${height}px`;
        // Read by the comment indicator triangle in customGridStyles.css. Always half the
        // field's own rendered height, so it stays proportionate to the field at any zoom level
        // instead of a fixed size over/under-covering it; capped at the 15px design-time size
        // (scaled by zoom) so it doesn't balloon on an unusually tall field.
        const indicatorSize = Math.min(COMMENT_INDICATOR_SIZE_PX * anchor.scale, height * 0.5);
        hitbox.style.setProperty('--comment-indicator-size', `${indicatorSize}px`);

        // Mirrors the report grid's cell comment indicator (customGridStyles.css) — a small
        // corner triangle, blue for unresolved / grey for resolved-only, drawn via ::after so
        // it stays anchored to the hitbox through zoom/scroll without any extra positioning math.
        if (field.overridable) {
            hitbox.classList.toggle('hasUnresolvedComments', field.hasUnresolvedComments);
            hitbox.classList.toggle('hasOnlyResolvedComments', !field.hasUnresolvedComments && field.hasOnlyResolvedComments);
            // Numeric fields are conventionally right-aligned, so their indicator goes top-left.
            hitbox.classList.toggle('hasCommentsIndicatorLeft', !!field.isNumeric);

            // Also mirror the grid's aria-label suffix (CommonIndividualReportTable.tsx) so the
            // comment state is announced to screen readers, not just shown via the classes above.
            // Prefer the cell's displayed value (what the grid announces for the same cell) over
            // the tooltip/internal field name - a raw AcroForm field name reads as noise to a
            // screen reader user.
            const baseLabel = field.cellDisplayValue || field.tooltip || field.name;
            if (field.hasUnresolvedComments) {
                hitbox.setAttribute('aria-label', `${baseLabel} ${i18n.t('hasUnresolvedComments')}`);
            } else if (field.hasOnlyResolvedComments) {
                hitbox.setAttribute('aria-label', `${baseLabel} ${i18n.t('hasResolvedComments')}`);
            } else {
                hitbox.removeAttribute('aria-label');
            }
        }

        // The statement icon overlay owns the hitbox's final look (it hides the box and
        // shrinks it to the icon's footprint), so hand it over after the geometry is set.
        if (field.isStatement && handlers.onStatementFieldElement) {
            handlers.onStatementFieldElement(field, hitbox);
        }
    });
};

export const removeAllInteractiveFieldHitboxes = (viewerElementId: string): void => {
    const pdfViewerContainer = document.getElementById(viewerElementId);
    if (!pdfViewerContainer) {
        return;
    }

    pdfViewerContainer.querySelectorAll(`[${HITBOX_ATTRIBUTE}]`).forEach((hitbox) => hitbox.remove());
};
